import type { IShortcutListSection } from '../../../contexts/AppShortcutsContext'
import { isSingleKeyShortcutBlocked } from '../../../helpers/keyboardShortcuts'

/** Opens the page's own actions menu from anywhere on the page, as Gmail's "More actions" does. */
export const PAGE_ACTIONS_KEY = '.'

/** The `?` shortcut list's entry for the key, shown while a page actions menu is mounted. */
export const PAGE_ACTIONS_SHORTCUT_SECTION: IShortcutListSection = {
  title: 'Actions menu',
  shortcuts: [{ id: 'pageActions', label: 'Open the actions menu', chords: [[PAGE_ACTIONS_KEY]] }],
}

/** A page actions trigger's tooltip: its name plus the key, so the key can be found unasked. */
export const pageActionsTitle = (name: string): string => `${name} (${PAGE_ACTIONS_KEY})`

type OpenPageActions = () => void

// A stack rather than a single slot: when two headers overlap during a route change, the
// newest answers the key, and unmounting it hands the key back to the one below.
const openers: OpenPageActions[] = []

const handleKeyDown = (event: KeyboardEvent) => {
  const open = openers[openers.length - 1]
  if (!open || event.key !== PAGE_ACTIONS_KEY || event.repeat || event.defaultPrevented) return
  if (isSingleKeyShortcutBlocked(event)) return
  event.preventDefault()
  open()
}

/** Puts the page actions key on `open` until the returned cleanup runs. */
export const registerPageActionsMenu = (open: OpenPageActions): (() => void) => {
  if (!openers.length) window.addEventListener('keydown', handleKeyDown)
  openers.push(open)

  return () => {
    const index = openers.lastIndexOf(open)
    if (index !== -1) openers.splice(index, 1)
    if (!openers.length) window.removeEventListener('keydown', handleKeyDown)
  }
}

const isTypingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  !!target.closest('input, textarea, select, [contenteditable="true"]')

/**
 * Runs the item whose `shortcut` matches the key, through its own click handler, so a
 * shortcut does exactly what a click does — link items included. Returns whether it ran.
 */
export const runMenuItemShortcut = (event: {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  target: EventTarget | null
  currentTarget: HTMLElement
}): boolean => {
  if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return false
  if (isTypingTarget(event.target)) return false

  const key = event.key.toLowerCase()
  const item = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>('[data-menu-shortcut]'),
  ).find(
    (element) =>
      element.dataset.menuShortcut === key && element.getAttribute('aria-disabled') !== 'true',
  )
  if (!item) return false

  item.click()
  return true
}
